import { msg } from '@lingui/core/macro';
import { showToast } from 'state/toast';
import { State } from 'utils/state';
import { IToastProps } from 'components/Toast';
import { getStorageItem, setStorageItem } from './storage';

const deviceInUseToast: IToastProps = {
  title: msg`Camera or microphone in use`,
  message: msg`Your camera or microphone is currently being used by another application. Please close other apps like Zoom, Teams, or Skype and try again.`,
  type: 'warn',
  notificationId: 'WARN_MEDIA_DEVICE_IN_USE',
};

const mediaDevicesToastWarnings: Record<string, IToastProps> = {
  NotAllowedError: {
    title: msg`VOIP not available`,
    message: msg`You have denied access to your camera and microphone.`,
    type: 'warn',
    notificationId: 'WARN_MEDIA_NOT_ALLOWED',
  },
  NotSupportedError: {
    title: msg`VOIP not available`,
    message: msg`Your browser does not support camera and microphone recording.`,
    type: 'warn',
    notificationId: 'WARN_MEDIA_NOT_SUPPORTED',
  },
  NotReadableError: deviceInUseToast,
  TrackStartError: deviceInUseToast, // Variant of NotReadableError on Chrome
  NotFoundError: {
    title: msg`No camera or microphone found`,
    message: msg`We couldn't detect a camera or microphone on your device. Please connect one and try again.`,
    type: 'warn',
    notificationId: 'WARN_MEDIA_NOT_FOUND',
  },
  OverconstrainedError: {
    title: msg`Device doesn't meet requirements`,
    message: msg`Your camera or microphone doesn't support the required settings. Try using a different device.`,
    type: 'warn',
    notificationId: 'WARN_MEDIA_OVERCONSTRAINED',
  },
  default: {
    title: msg`VOIP not available`,
    message: msg`Failed to initialize media devices.`,
    type: 'warn',
    notificationId: 'WARN_MEDIA_INIT_ERROR',
  },
};

export type VoipMediaState = {
  microphoneEnabled: boolean;
  cameraEnabled: boolean;
};

export type Stream = MediaStream | string | null;

export interface MediaDeviceConfig {
  video?: boolean;
  audio?: boolean;
  preferredCameraId?: string;
  preferredAudioId?: string;
}

export interface StreamState extends MediaDeviceConfig {
  streamId: string | null;
  isVideoEnabled: boolean;
  isAudioEnabled: boolean;
  isVideoAllowed: boolean;
  isAudioAllowed: boolean;
  isStreaming: boolean;
  initialized: boolean;
  mediaAllowed: boolean;
  error?: string;
}

interface DeviceInfo {
  id: string;
  label: string;
}

/**
 * This class connects to the user's media devices (camera and microphone) and provides methods to
 * toggle them on and off. It also saves the user's preferences for the media devices.
 *
 * @example
 * ```typescript
 * const mediaManager = new MediaDeviceManager();
 *
 * try {
 *   const stream = await mediaManager.init({ // Initialize with both video and audio
 *     video: true,
 *     audio: true,
 *   });
 *
 *   const videoElement = document.querySelector<HTMLVideoElement>('#videoElement');
 *   if (videoElement) {
 *     videoElement.srcObject = stream; // Attach stream to video element
 *     videoElement.play();
 *   }
 * } catch (error) {
 *   console.error('Failed to initialize media devices:', error);
 * }
 * ```
 */
export class MediaDeviceManager extends State<StreamState> {
  public stream: MediaStream | null = null;
  private readonly storageKey: string = 'mediaDevicePreferences';

  private availableDevices: {
    videoDevices: DeviceInfo[];
    audioDevices: DeviceInfo[];
  } | null = null;

  public permissions: {
    video: boolean;
    audio: boolean;
  } | null = null;

  // initialization is async. This flag helps us prevent multiple initializations in parallel
  private deviceThenable: false | Promise<MediaStream> = false;

  constructor() {
    super();
    this.checkBrowserSupport();

    this.on('change', () => {
      this.savePreferences(this.get() as StreamState);
    });
  }

  private checkBrowserSupport(): void {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.setValues({ mediaAllowed: false });
      showToast(mediaDevicesToastWarnings.NotSupportedError);
    } else {
      this.setValues({ mediaAllowed: true });
    }
  }

  public async init(userConfig?: Partial<MediaDeviceConfig>): Promise<MediaStream | undefined> {
    if (!this.get('mediaAllowed')) {
      return;
    }

    const [notAllowedToProceed] = this.emit('before-initialized', this);
    if (notAllowedToProceed === true) {
      return;
    }

    if (this.stream) {
      return this.stream;
    }

    this.checkPermissions();
    const preferences = await this.getPreferences();

    // Override preferences based on browser permissions
    const config: MediaDeviceConfig = {
      ...preferences,
      ...userConfig,
      video: userConfig?.video !== undefined ? userConfig.video : preferences.video,
      audio: userConfig?.audio !== undefined ? userConfig.audio : preferences.audio,
    };

    this.setValues(config);

    this.emit('initialized', this);

    return this.startStream();
  }

  public async getAvailableDevices(): Promise<{
    videoDevices: DeviceInfo[];
    audioDevices: DeviceInfo[];
  }> {
    const devices = await navigator.mediaDevices.enumerateDevices();

    this.availableDevices = {
      videoDevices: devices
        .filter((device) => device.kind === 'videoinput')
        .map((device) => ({
          id: device.deviceId,
          label: device.label,
        })),
      audioDevices: devices
        .filter((device) => device.kind === 'audioinput')
        .map((device) => ({
          id: device.deviceId,
          label: device.label,
        })),
    };

    return this.availableDevices;
  }

  public toggleVideo(): boolean {
    if (!this.stream) return false;
    const newValue = !this.get('isVideoEnabled');
    this.enableTrack('video', newValue);
    this.setValues({ isVideoEnabled: newValue });
    return newValue;
  }

  public toggleAudio(): boolean {
    if (!this.stream) return false;
    const newValue = !this.get('isAudioEnabled');
    this.enableTrack('audio', newValue);
    this.setValues({ isAudioEnabled: newValue });
    return newValue;
  }

  public enableTrack(type: 'audio' | 'video', enable: boolean): void {
    const tracks = type === 'audio' ? this.stream?.getAudioTracks() : this.stream?.getVideoTracks();
    tracks?.forEach((track) => {
      track.enabled = enable;
      if (enable) {
        this.reinitializeTrack(type);
      } else {
        track.stop();
        this.emit('track:stopped', track);
      }
    });
  }

  async reinitializeTrack(type: 'audio' | 'video') {
    const newStream = await this.initDevice({ [type]: true });
    const newTracks = type === 'audio' ? newStream.getAudioTracks() : newStream.getVideoTracks();
    const newTrack = newTracks[0];
    const existingTracks = type === 'audio' ? this.stream?.getAudioTracks() : this.stream?.getVideoTracks();
    if (newTrack && this.stream) {
      this.stream.addTrack(newTrack);
    }
    existingTracks?.forEach((track) => {
      if (track.readyState === 'ended') {
        this.stream?.removeTrack(track);
        this.emit('track:removed', track);
      }
    });
    this.emit('track:added', newTrack, type);
  }

  public stopStream(): void {
    if (!this.stream) return;
    this.setValues({
      isStreaming: false,
    });
    this.stream.getTracks().forEach((track) => {
      track.stop();
      this.emit('track:stopped', track);
    });
    this.emit('stream:stopped', this);
    this.stream = null;
  }

  private async initDevice(constraints?: MediaStreamConstraints) {
    if (this.deviceThenable) {
      // ensure we get access to devices only once
      return this.deviceThenable;
    }

    const config = this.get() as StreamState;

    constraints = constraints ?? {
      video:
        config.video === undefined
          ? true
          : config.preferredCameraId
            ? { deviceId: { exact: config.preferredCameraId } }
            : config.video === false
              ? false
              : true,

      audio:
        config.audio === undefined
          ? true
          : config.preferredAudioId
            ? { deviceId: { exact: config.preferredAudioId } }
            : config.audio === false
              ? false
              : true,
    };

    this.deviceThenable = navigator.mediaDevices.getUserMedia(constraints);
    try {
      const result = await this.deviceThenable;
      return result;
    } finally {
      this.deviceThenable = false;
    }
  }

  public async startStream() {
    if (!this.get('mediaAllowed')) {
      return;
    }
    if (this.stream) return;

    const config = this.get() as StreamState;

    try {
      this.stream = await this.initDevice();

      // if we made it to this point, then we know that the user has allowed the browser to access the camera and microphone
      this.setValues({
        isStreaming: true,
        initialized: true,
        streamId: this.stream.id,
        // we can assume the availability of the media devices to true, but we will also check for permissions
        isVideoAllowed: true,
        isAudioAllowed: true,
      });

      // we check to make sure mic and camera are enabled. Some older devices will fail here which is why we made the assumption above
      this.checkPermissions();

      // Update preferences with actual stream capabilities
      const actualConfig = {
        ...config,
        video: this.stream.getVideoTracks().length > 0,
        audio: this.stream.getAudioTracks().length > 0,
      };

      this.savePreferences(actualConfig);
      this.emit('stream:started', this);
      return this.stream;
    } catch (error) {
      // Update preferences if permissions are denied
      const updatedConfig = {
        ...config,
        video: false,
        audio: false,
      };
      this.savePreferences(updatedConfig);
      console.error(`Failed to initialize media devices: ${error}`);
      this.emit('stream:error', error);
      if (mediaDevicesToastWarnings[(error as Error).name]) {
        showToast(mediaDevicesToastWarnings[(error as Error).name]);
      } else {
        showToast(mediaDevicesToastWarnings.default);
      }
    }
  }

  private savePreferences(config: MediaDeviceConfig): void {
    setStorageItem('localStorage', this.storageKey, config);
  }

  public async getPreferences(): Promise<MediaDeviceConfig> {
    const preferences = getStorageItem('localStorage', this.storageKey);

    if (preferences) {
      return preferences;
    }

    if (!this.availableDevices) {
      await this.getAvailableDevices();
    }

    const defaultPreferences = {
      video: true,
      audio: true,
    };

    return defaultPreferences;
  }

  public getDefaultVideoDeviceId(): string {
    return this.availableDevices?.videoDevices[0]?.id || '';
  }

  public getDefaultAudioDeviceId(): string {
    return this.availableDevices?.audioDevices[0]?.id || '';
  }

  public checkPermissions(): void {
    try {
      navigator.permissions.query({ name: 'camera' as PermissionName }).then((result) => {
        this.permissions = {
          video: result.state === 'granted',
          audio: this.permissions?.audio || false,
        };

        this.setValues({ isVideoAllowed: result.state === 'granted' });
      });

      navigator.permissions.query({ name: 'microphone' as PermissionName }).then((result) => {
        this.permissions = {
          audio: result.state === 'granted',
          video: this.permissions?.video || false,
        };

        this.setValues({ isAudioAllowed: result.state === 'granted' });
      });
    } catch {
      // It looks like the browser doesn't support permissions query
    }
  }

  destroy() {
    this.stream?.getTracks().forEach((track) => {
      track.stop();
      this.stream?.removeTrack(track);
    });

    this.stopStream();
    this.emit('destroyed');
    setTimeout(() => super.destroy(), 0);
  }
}
